import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { veiculoService } from '../../services/api';
import Loading from '../../components/common/Loading';

const HistoricoPontos = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    page: 1,
    limit: 20
  });

  // Buscar dados do veículo e histórico
  const { data: veiculoData, isLoading: loadingVeiculo } = useQuery({
    queryKey: ['veiculo', id],
    queryFn: () => veiculoService.buscar(id),
    enabled: !!id
  });

  const { data: historicoData, isLoading: loadingHistorico } = useQuery({
    queryKey: ['veiculo-historico', id, filtros],
    queryFn: () => veiculoService.historicoPontos(id, filtros),
    enabled: !!id
  });

  const veiculo = veiculoData?.data?.data;
  const pagination = historicoData?.data?.data?.pagination;

  // Calcular estatísticas
  const estatisticas = React.useMemo(() => {
    const historico = historicoData?.data?.data?.historico || [];
    if (!historico.length) return null;

    const totalPontos = historico.length;
    const totalKm = historico.reduce((acc, ponto) => acc + (ponto.odometro || 0), 0);
    const kmMedio = totalKm / totalPontos;
    
    // Agrupar por colaborador
    const porColaborador = historico.reduce((acc, ponto) => {
      const nome = ponto.colaborador?.nome || 'Desconhecido';
      acc[nome] = (acc[nome] || 0) + 1;
      return acc;
    }, {});

    // Agrupar por mês
    const porMes = historico.reduce((acc, ponto) => {
      const mes = new Date(ponto.data).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
      acc[mes] = (acc[mes] || 0) + 1;
      return acc;
    }, {});

    return {
      totalPontos,
      totalKm,
      kmMedio: Math.round(kmMedio),
      porColaborador,
      porMes
    };
  }, [historicoData]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor,
      page: 1 // Reset para primeira página
    }));
  };

  const handlePageChange = (novaPage) => {
    setFiltros(prev => ({ ...prev, page: novaPage }));
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatarOdometro = (km) => {
    return km ? `${km.toLocaleString('pt-BR')} km` : '-';
  };

  if (loadingVeiculo || loadingHistorico) {
    return <Loading />;
  }

  if (!veiculo) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Veículo não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Frota</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              Histórico de Pontos - {veiculo.placa}
            </h1>
            <p className="text-indigo-200 mt-1 text-sm">
              {veiculo.marca} {veiculo.modelo} • {veiculo.tipo.toUpperCase()}
            </p>
          </div>
          <button
            onClick={() => navigate('/frota/veiculos')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white px-4 py-2 rounded-xl transition-colors"
          >
            ← Voltar
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Total de Pontos</h3>
            <p className="text-2xl font-bold text-blue-600">{estatisticas.totalPontos}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Quilometragem Total</h3>
            <p className="text-2xl font-bold text-green-600">{formatarOdometro(estatisticas.totalKm)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Média por Ponto</h3>
            <p className="text-2xl font-bold text-purple-600">{formatarOdometro(estatisticas.kmMedio)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Colaboradores</h3>
            <p className="text-2xl font-bold text-orange-600">{Object.keys(estatisticas.porColaborador).length}</p>
          </div>
        </div>
      )}

      {/* Gráficos */}
      {estatisticas && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico por Colaborador */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pontos por Colaborador</h3>
            <div className="space-y-3">
              {Object.entries(estatisticas.porColaborador)
                .sort(([,a], [,b]) => b - a)
                .map(([colaborador, pontos]) => (
                  <div key={colaborador} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{colaborador}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ 
                            width: `${(pontos / Math.max(...Object.values(estatisticas.porColaborador))) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">{pontos}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Gráfico por Mês */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pontos por Mês</h3>
            <div className="space-y-3">
              {Object.entries(estatisticas.porMes)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([mes, pontos]) => (
                  <div key={mes} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{mes}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ 
                            width: `${(pontos / Math.max(...Object.values(estatisticas.porMes))) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">{pontos}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Início
            </label>
            <input
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Fim
            </label>
            <input
              type="date"
              value={filtros.dataFim}
              onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFiltros({ dataInicio: '', dataFim: '', page: 1, limit: 20 })}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de Histórico */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Histórico de Pontos</h3>
        </div>
        
        {(historicoData?.data?.data?.historico || []).length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            Nenhum ponto registrado para este veículo
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Colaborador
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Odômetro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Número OS
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Localização
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(historicoData?.data?.data?.historico || []).map((ponto, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatarData(ponto.data)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ponto.colaborador?.nome || 'Desconhecido'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatarOdometro(ponto.odometro)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ponto.numeroOS || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          ponto.tipo === 'batida' 
                            ? 'bg-blue-100 text-blue-800'
                            : ponto.tipo === 'viagem-inicio'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {ponto.tipo === 'batida' ? 'Batida' : 
                           ponto.tipo === 'viagem-inicio' ? 'Início Viagem' : 'Fim Viagem'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {ponto.localizacao?.endereco || 
                         `${ponto.localizacao?.latitude?.toFixed(4)}, ${ponto.localizacao?.longitude?.toFixed(4)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pagination && pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                  {pagination.total} resultados
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md">
                    {pagination.page}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HistoricoPontos;
